import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { adminOrderService } from "../../../services/adminOrderService";
import { AdminOrderList, PAYMENT_STATUSES } from "../../../types/adminOrders";

interface PaymentStatusUpdateModalProps {
  open: boolean;
  onClose: () => void;
  order: AdminOrderList | null;
  onUpdate: () => void;
}

const PaymentStatusUpdateModal: React.FC<PaymentStatusUpdateModalProps> = ({
  open,
  onClose,
  order,
  onUpdate,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<string>("");
  const [verificationAction, setVerificationAction] = useState<
    "verify" | "reject" | "update"
  >("update");
  const [notes, setNotes] = useState<string>("");
  const [needsVerification, setNeedsVerification] = useState(false);

  useEffect(() => {
    if (order) {
      setPaymentStatus(order.paymentStatus || "");
      setNotes("");
      setError(null);

      // Check if this is a manual payment (Alif/DC Bank)
      const isManual =
        order.paymentMethodType === "Alif" ||
        order.paymentMethodType === "DC Card" ||
        order.paymentMethodType === "DcCard" ||
        order.paymentMethodType === "2" ||
        order.paymentMethodType === "3";

      // Check if payment needs verification - for manual payments that are pending or not yet verified
      const needsVerif =
        isManual &&
        (order.paymentStatus?.toLowerCase() === "pending_verification" ||
          order.paymentStatus?.toLowerCase() === "processing" ||
          order.paymentStatus?.toLowerCase() === "pending verification" ||
          order.paymentStatus?.toLowerCase() === "pending");

      setNeedsVerification(needsVerif);

      // Determine default action based on payment type and status
      if (needsVerif) {
        setVerificationAction("verify");
      } else {
        setVerificationAction("update");
      }
    }
  }, [order]);

  const handleSubmit = async () => {
    if (!order) return;

    setLoading(true);
    setError(null);

    try {
      // For manual payments that need verification, use verify-payment endpoint
      if (needsVerification && verificationAction !== "update") {
        // Use order ID directly (AdminOrderList has id field)
        await adminOrderService.verifyManualPayment(
          order.id,
          verificationAction === "verify",
          notes || undefined
        );
      } else {
        // For other cases, use the general payment status update endpoint
        await adminOrderService.updateOrderPaymentStatus(order.orderNumber, {
          paymentStatus: paymentStatus,
          transactionId: "", // Will be preserved by backend
          paymentDate: new Date().toISOString(),
          notes: notes || "",
        });
      }

      // Close modal first, then refresh data
      onClose();

      // Refresh data after modal closes
      onUpdate();
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          t("admin.orders.failedToUpdatePaymentStatus")
      );
      setLoading(false);
    }
  };

  if (!order) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("admin.orders.updatePaymentStatus")} - {order.orderNumber}</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* Payment Method Info */}
          <Box sx={{ mb: 3, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              {t("admin.orders.paymentMethod")}
            </Typography>
            <Typography variant="body1" fontWeight="medium">
              {order.paymentMethodType || t("admin.orders.unknown")}
            </Typography>
            {order.paymentStatus && (
              <>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ mt: 1 }}
                >
                  {t("admin.orders.currentStatus")}
                </Typography>
                <Typography variant="body2">{order.paymentStatus}</Typography>
              </>
            )}
          </Box>

          {/* Manual Payment Verification Section */}
          {needsVerification && (
            <>
              <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
                <FormLabel component="legend">{t("admin.orders.verificationAction")}</FormLabel>
                <RadioGroup
                  value={verificationAction}
                  onChange={(e) =>
                    setVerificationAction(
                      e.target.value as "verify" | "reject" | "update"
                    )
                  }
                >
                  <FormControlLabel
                    value="verify"
                    control={<Radio />}
                    label={t("admin.orders.verifyPaymentApprove")}
                  />
                  <FormControlLabel
                    value="reject"
                    control={<Radio />}
                    label={t("admin.orders.rejectPayment")}
                  />
                  <FormControlLabel
                    value="update"
                    control={<Radio />}
                    label={t("admin.orders.updateStatusManually")}
                  />
                </RadioGroup>
              </FormControl>

              {verificationAction === "update" && (
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel>{t("admin.orders.paymentStatus")}</InputLabel>
                  <Select
                    value={paymentStatus}
                    onChange={(e) => setPaymentStatus(e.target.value)}
                    label={t("admin.orders.paymentStatus")}
                  >
                    {PAYMENT_STATUSES.map((status) => (
                      <MenuItem key={status} value={status}>
                        {status}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </>
          )}

          {/* Regular Payment Status Update (for Stripe or already verified payments) */}
          {!needsVerification && (
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>{t("admin.orders.paymentStatus")}</InputLabel>
              <Select
                value={paymentStatus}
                onChange={(e) => setPaymentStatus(e.target.value)}
                label={t("admin.orders.paymentStatus")}
              >
                {PAYMENT_STATUSES.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Notes Field */}
          <TextField
            fullWidth
            multiline
            rows={4}
            label={t("admin.orders.notesOptional")}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t("admin.orders.paymentNotesPlaceholder")}
            sx={{ mb: 2 }}
          />

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {t("common.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            loading || (!paymentStatus && verificationAction === "update")
          }
        >
          {loading
            ? t("admin.orders.updating")
            : needsVerification && verificationAction !== "update"
              ? verificationAction === "verify"
                ? t("admin.orders.verifyPayment")
                : t("admin.orders.rejectPayment")
              : t("admin.orders.updateStatus")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentStatusUpdateModal;
